import { pathOr } from 'ramda';

import {
  getPart,
  getQuoteItemById,
  getQuoteItemByIdDenormalized,
} from '../selectors';
import {
  getComponentHasPricing,
  getContainsVirusByComponentId,
  getFilenameByComponentId,
  getHasChildrenByComponentId,
  getHasProcessByComponentId,
  getHasNon100PctYield,
  getIsAssemblyByComponentId,
  getPartNumberByComponentId,
  getPartUuidByComponentId,
  getRevisionByComponentId,
} from './components';

export const doesQuoteItemHaveExpedites = (state, quoteItemId) =>
  pathOr(
    [],
    ['rootComponent', 'quantities'],
    getQuoteItemByIdDenormalized(state, quoteItemId),
  ).some((qty) => pathOr([], ['expedites'], qty).length > 0);

export const getRootComponentIdByQuoteItemId = (state, quoteItemId) =>
  pathOr(null, ['rootComponent'], getQuoteItemById(state, quoteItemId));

export const getPartUuidByQuoteItemId = (state, quoteItemId) =>
  getPartUuidByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getPartIsModifiableByQuoteItemId = (state, quoteItemId) =>
  pathOr(true, ['isModifiable'], getQuoteItemById(state, quoteItemId));

export const getAnnotationsCountByQuoteItemId = (state, quoteItemId) =>
  pathOr(
    0,
    ['annotationsCount'],
    getPart(state, getPartUuidByQuoteItemId(state, quoteItemId)),
  );

export const getFilenameByQuoteItemId = (state, quoteItemId) =>
  getFilenameByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getContainsVirusByQuoteItemId = (state, quoteItemId) =>
  getContainsVirusByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getIsQuoteItemExportControlled = (state, quoteItemId) =>
  pathOr(false, ['exportControlled'], getQuoteItemById(state, quoteItemId));

export const getIsViewerDataPurgedByQuoteItemId = (state, quoteItemId) =>
  pathOr(
    false,
    ['isViewerDataPurged'],
    getPart(state, getPartUuidByQuoteItemId(state, quoteItemId)),
  );

export const getIsManualPart = (state, quoteItemId) =>
  getPart(state, getPartUuidByQuoteItemId(state, quoteItemId))?.isManual ??
  false;

export const getPartNumberByQuoteItemId = (state, quoteItemId) =>
  getPartNumberByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getRevisionByQuoteItemId = (state, quoteItemId) =>
  getRevisionByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getRootComponentIsAssemblyByQuoteItemId = (state, quoteItemId) =>
  getIsAssemblyByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getRootComponentHasChildrenByQuoteItemId = (state, quoteItemId) =>
  getHasChildrenByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getPrimaryFileUuidByQuoteItemId = (state, quoteItemId) =>
  pathOr(
    null,
    ['primaryFileUuid'],
    getPart(state, getPartUuidByQuoteItemId(state, quoteItemId)),
  );

export const getWorkflowStatusByQuoteItemId = (state, quoteItemId) =>
  pathOr(undefined, ['workflowStatus'], getQuoteItemById(state, quoteItemId));

export const getPartMasterUuidByQuoteItemId = (state, quoteItemId) =>
  pathOr(null, ['partMasterUuid'], getQuoteItemById(state, quoteItemId));

export const getPartMasterVersionByQuoteItemId = (state, quoteItemId) =>
  pathOr(null, ['partMasterVersion'], getQuoteItemById(state, quoteItemId));

export const getWorkflowStepsByQuoteItemId = (state, quoteItemId) =>
  pathOr({}, ['workflowSteps'], getQuoteItemById(state, quoteItemId));

export const getHasMultiComponentNestByQuoteItemId = (state, quoteItemId) =>
  pathOr(
    false,
    ['hasMultiComponentNest'],
    getQuoteItemById(state, quoteItemId),
  );

export const getIsNotRemovableByQuoteItemId = (state, quoteItemId) =>
  pathOr(false, ['isNotRemovable'], getQuoteItemById(state, quoteItemId));

export const getIsBulkUpdateDisabledByQuoteItemId = (state, quoteItemId) =>
  pathOr(false, ['isBulkUpdateDisabled'], getQuoteItemById(state, quoteItemId));

export const getIsReplaceAssemblyDisabledByQuoteItemId = (state, quoteItemId) =>
  pathOr(
    false,
    ['isReplaceAssemblyDisabled'],
    getQuoteItemById(state, quoteItemId),
  );

export const getRootComponentHasProcessByQuoteItemId = (state, quoteItemId) =>
  getHasProcessByComponentId(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

export const getProcessingFailedByQuoteItemId = (state, quoteItemId) =>
  getPart(state, getPartUuidByQuoteItemId(state, quoteItemId))
    ?.processingFailed;

export const getHasFailedChildrenByQuoteItemId = (state, quoteItemId) =>
  getPart(state, getPartUuidByQuoteItemId(state, quoteItemId))
    ?.hasFailedChildren;

export const getShouldShowBomBuilderPricingAlert = (state, quoteItemId) => {
  const { componentIds, rootComponent: rootComponentId } = getQuoteItemById(
    state,
    quoteItemId,
  );
  const nonRootComponentIds = componentIds.filter(
    (id) => id !== rootComponentId,
  );

  const hasPricing = getComponentHasPricing(state, rootComponentId);
  const hasComponentWithNon100PctYield = nonRootComponentIds.some(
    (componentId) => getHasNon100PctYield(state, componentId),
  );
  return hasPricing || hasComponentWithNon100PctYield;
};
